import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  RadialBarChart,
  RadialBar,
} from "recharts";
import {
  ArrowTrendingUpIcon,
  UsersIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  StarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ArrowDownCircleIcon,
  UserIcon,
  ClockIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  ChartPieIcon,
  EnvelopeIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

// 🎨 Updated Professional Color Palettes with cool, non-white/dark backgrounds
const THEME_COLORS = {
  dark: {
    bg: "bg-slate-950",
    text: "text-gray-100",
    cardBg: "bg-slate-900",
    cardBorder: "border-slate-800",
    chartStroke: "#4b5563",
    axisStroke: "#94a3b8",
    // Fixed Tooltip Colors for better contrast
    tooltipBg: "bg-gray-800",
    tooltipBorder: "border-gray-700",
    tooltipText: "text-gray-100", // Ensures text is readable
    // New, high-contrast gender colors for dark mode
    gender: ["#60a5fa", "#3b82f6"],
    headcountActive: "#4f46e5",
    headcountResigned: "#ef4444",
    performance: ["#10b981", "#f59e0b", "#ef4444"],
  },
  light: {
    bg: "bg-slate-100",
    text: "text-gray-900",
    cardBg: "bg-white",
    cardBorder: "border-gray-200",
    chartStroke: "#e5e7eb",
    axisStroke: "#9ca3af",
    // Fixed Tooltip Colors for better contrast
    tooltipBg: "bg-white",
    tooltipBorder: "border-gray-200",
    tooltipText: "text-gray-900", // Ensures text is readable
    // New, high-contrast gender colors for light mode
    gender: ["#1e3a8a", "#3b82f6"],
    headcountActive: "#4f46e5",
    headcountResigned: "#ef4444",
    performance: ["#10b981", "#f59e0b", "#ef4444"],
  },
};

// 📊 Sample Data - Updated for a more realistic feel
const employeeData = [
  { name: "Jan", Active: 1200, Resigned: 80 },
  { name: "Feb", Active: 1220, Resigned: 75 },
  { name: "Mar", Active: 1210, Resigned: 90 },
  { name: "Apr", Active: 1250, Resigned: 85 },
  { name: "May", Active: 1280, Resigned: 100 },
  { name: "Jun", Active: 1300, Resigned: 95 },
];

const attritionData = [
  { name: "Marketing", Attrition: 5 },
  { name: "Sales", Attrition: 8 },
  { name: "HR", Attrition: 2 },
  { name: "IT", Attrition: 3 },
  { name: "Finance", Attrition: 4 },
];

const genderData = [
  { name: "Male", value: 65 },
  { name: "Female", value: 35 },
];

const recruitmentData = [
  { name: "Applications", value: 500 },
  { name: "Shortlisted", value: 250 },
  { name: "Interviews", value: 120 },
  { name: "Offers", value: 60 },
  { name: "Joined", value: 45 },
];

const performanceData = [
  { name: "High", value: 25 },
  { name: "Average", value: 60 },
  { name: "Low", value: 15 },
];

// 🔹 Reusable Stat Card Component - Now accepts theme
const StatCard = ({ title, value, change, icon, iconBg, description, theme }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    whileHover={{ scale: 1.05, boxShadow: theme === "dark" ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)" : "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
    className={`${theme === "dark" ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-6 rounded-2xl shadow-xl transition-all duration-300 transform-gpu cursor-pointer border ${theme === "dark" ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder} ${theme === "dark" ? THEME_COLORS.dark.text : THEME_COLORS.light.text}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"} font-medium`}>{title}</p>
        <h2 className="text-3xl font-bold mt-1">{value}</h2>
      </div>
      <div className={`p-4 rounded-full shadow-lg ${iconBg}`}>
        {icon}
      </div>
    </div>
    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{description}</p>
    {change && (
      <div className="flex items-center text-sm mt-2">
        <span className={`font-bold ${change.includes("+") ? "text-green-500" : "text-red-500"}`}>{change}</span>
        <span className={`ml-1 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>vs. last month</span>
      </div>
    )}
  </motion.div>
);

// 🔹 Reusable Custom Tooltip for Charts - Now dynamic and readable
const CustomTooltip = ({ active, payload, label, theme }) => {
  if (active && payload && payload.length) {
    return (
      <div className={`${theme === "dark" ? THEME_COLORS.dark.tooltipBg : THEME_COLORS.light.tooltipBg} p-4 rounded-lg shadow-xl border ${theme === "dark" ? THEME_COLORS.dark.tooltipBorder : THEME_COLORS.light.tooltipBorder} backdrop-blur-sm bg-opacity-80`}>
        <p className={`font-bold text-lg ${theme === "dark" ? THEME_COLORS.dark.tooltipText : THEME_COLORS.light.tooltipText}`}>{label}</p>
        {payload.map((p, index) => (
          <p key={index} className={`text-sm ${theme === "dark" ? THEME_COLORS.dark.tooltipText : THEME_COLORS.light.tooltipText}`}>
            <span className="font-semibold">{p.name}:</span> {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// 🔹 Reusable Section Header with Animation
const SectionTitle = ({ title, subtitle, theme }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className="text-center mb-10"
  >
    <h2 className={`text-4xl font-extrabold tracking-tight ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}>{title}</h2>
    <p className={`mt-2 text-lg ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{subtitle}</p>
  </motion.div>
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// 🔹 Main Dashboard Component
const MISDashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for a saved theme preference
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? JSON.parse(savedTheme) : true; // Default to dark mode
  });

  useEffect(() => {
    // Update localStorage whenever the theme changes
    localStorage.setItem('theme', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const currentTheme = isDarkMode ? "dark" : "light";

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      // This is the main background color for the entire dashboard
      className={`min-h-screen p-8 font-sans transition-colors duration-500 ${isDarkMode ? THEME_COLORS.dark.bg : THEME_COLORS.light.bg} ${isDarkMode ? THEME_COLORS.dark.text : THEME_COLORS.light.text}`}
    >
      <div className="container mx-auto max-w-7xl relative">
        {/* Dark/Light Mode Toggle */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 rounded-full shadow-lg transition-colors duration-300 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900 focus:ring-gray-600' : 'focus:ring-offset-gray-100 focus:ring-gray-300'}`}
          >
            {isDarkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
          </button>
        </div>

        {/* Dashboard Title */}
        <SectionTitle
          title="Ultimate HR MIS Dashboard"
          subtitle="A powerful, data-driven overview of your workforce. Designed for clarity and impact."
          theme={currentTheme}
        />

        {/* Workforce Analytics */}
        <section className="mb-16">
          <motion.h3
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className={`text-3xl font-bold mb-8 flex items-center ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          >
            <UserGroupIcon className={`h-8 w-8 mr-3 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            Workforce Analytics
          </motion.h3>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <motion.div variants={itemVariants}>
              <StatCard
                title="Total Headcount"
                value="1,300"
                change="+5%"
                icon={<UsersIcon className={`h-8 w-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />}
                iconBg={isDarkMode ? "bg-blue-900" : "bg-blue-200"}
                description="Total number of active employees"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Employee Attrition"
                value="7.31%"
                change="-1.2%"
                icon={<ArrowTrendingUpIcon className={`h-8 w-8 ${isDarkMode ? "text-red-400" : "text-red-600"} rotate-90`} />}
                iconBg={isDarkMode ? "bg-red-900" : "bg-red-200"}
                description="Turnover rate this month"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Average Tenure"
                value="3.2 years"
                icon={<ClockIcon className={`h-8 w-8 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />}
                iconBg={isDarkMode ? "bg-green-900" : "bg-green-200"}
                description="Average length of employment"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Gender Diversity"
                value="65% Male"
                icon={<UserIcon className={`h-8 w-8 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`} />}
                iconBg={isDarkMode ? "bg-purple-900" : "bg-purple-200"}
                description="Male vs. Female ratio"
                theme={currentTheme}
              />
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div variants={itemVariants} className={`${isDarkMode ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-8 rounded-2xl shadow-xl border ${isDarkMode ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder}`}>
              <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Headcount Trend</h4>
              <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Displays the change in active and resigned employees over time, providing a clear trend analysis.</p>
              <ResponsiveContainer width="100%" height={300}>
                {/* Updated to a LineChart for a better visual trend analysis */}
                <LineChart data={employeeData} margin={{ top: 5, right: 30, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? THEME_COLORS.dark.chartStroke : THEME_COLORS.light.chartStroke} vertical={false} />
                  <XAxis dataKey="name" stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                  <YAxis stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                  <Tooltip content={<CustomTooltip theme={currentTheme} />} />
                  <Legend />
                  <Line type="monotone" dataKey="Active" stroke={THEME_COLORS[currentTheme].headcountActive} dot={{ strokeWidth: 2, r: 4 }} />
                  <Line type="monotone" dataKey="Resigned" stroke={THEME_COLORS[currentTheme].headcountResigned} strokeDasharray="5 5" dot={{ strokeWidth: 2, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
            <motion.div variants={itemVariants} className={`${isDarkMode ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-8 rounded-2xl shadow-xl border ${isDarkMode ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder}`}>
              <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Gender Demographics</h4>
              <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Visualizes the percentage distribution of male and female employees in the organization.</p>
              <ResponsiveContainer width="100%" height={300}>
                {/* Updated to a PieChart for a more suitable visual representation */}
                <PieChart>
                  <Pie
                    data={genderData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={THEME_COLORS[currentTheme].gender[index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip theme={currentTheme} />} />
                  <Legend align="center" verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        </section>

        <hr className={`my-16 border-t-2 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`} />

        {/* Recruitment & Onboarding */}
        <section className="mb-16">
          <motion.h3
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`text-3xl font-bold mb-8 flex items-center ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          >
            <BriefcaseIcon className={`h-8 w-8 mr-3 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            Recruitment & Onboarding
          </motion.h3>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <motion.div variants={itemVariants}>
              <StatCard
                title="Open Positions"
                value="45"
                icon={<BriefcaseIcon className={`h-8 w-8 ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`} />}
                iconBg={isDarkMode ? "bg-yellow-900" : "bg-yellow-200"}
                description="Vacancies across all departments"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Time-to-Hire"
                value="28 days"
                icon={<ClockIcon className={`h-8 w-8 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`} />}
                iconBg={isDarkMode ? "bg-purple-900" : "bg-purple-200"}
                description="Average time to fill a position"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Offer Acceptance Rate"
                value="85%"
                icon={<ClipboardDocumentCheckIcon className={`h-8 w-8 ${isDarkMode ? "text-teal-400" : "text-teal-600"}`} />}
                iconBg={isDarkMode ? "bg-teal-900" : "bg-teal-200"}
                description="Successful offers this quarter"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Cost per Hire"
                value="$1,200"
                icon={<CurrencyDollarIcon className={`h-8 w-8 ${isDarkMode ? "text-orange-400" : "text-orange-600"}`} />}
                iconBg={isDarkMode ? "bg-orange-900" : "bg-orange-200"}
                description="Average cost per new employee"
                theme={currentTheme}
              />
            </motion.div>
          </motion.div>
          <motion.div variants={itemVariants} className={`${isDarkMode ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-8 rounded-2xl shadow-xl border ${isDarkMode ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder}`}>
            <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Recruitment Funnel</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={recruitmentData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRecruitment" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                <YAxis axisLine={false} tickLine={false} stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                <Tooltip content={<CustomTooltip theme={currentTheme} />} />
                <Bar dataKey="value" radius={[10, 10, 0, 0]} fill="url(#colorRecruitment)" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </section>

        <hr className={`my-16 border-t-2 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`} />

        {/* Employee Engagement & Performance */}
        <section className="mb-16">
          <motion.h3
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className={`text-3xl font-bold mb-8 flex items-center ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          >
            <StarIcon className={`h-8 w-8 mr-3 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            Engagement & Performance
          </motion.h3>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div variants={itemVariants}>
              <StatCard
                title="Employee Satisfaction"
                value="4.5/5"
                icon={<HandThumbUpIcon className={`h-8 w-8 ${isDarkMode ? "text-lime-400" : "text-lime-600"}`} />}
                iconBg={isDarkMode ? "bg-lime-900" : "bg-lime-200"}
                description="Recent survey average score"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="eNPS"
                value="+35"
                icon={<ChatBubbleLeftRightIcon className={`h-8 w-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />}
                iconBg={isDarkMode ? "bg-blue-900" : "bg-blue-200"}
                description="Employee Net Promoter Score"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Training Hours"
                value="24 hrs"
                icon={<AcademicCapIcon className={`h-8 w-8 ${isDarkMode ? "text-cyan-400" : "text-cyan-600"}`} />}
                iconBg={isDarkMode ? "bg-cyan-900" : "bg-cyan-200"}
                description="Average per employee this year"
                theme={currentTheme}
              />
            </motion.div>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div variants={itemVariants} className={`${isDarkMode ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-8 rounded-2xl shadow-xl border ${isDarkMode ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder}`}>
              <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Performance Distribution</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={performanceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    stroke="none"
                  >
                    {performanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={THEME_COLORS[currentTheme].performance[index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip theme={currentTheme} />} />
                  <Legend align="center" verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
            <motion.div variants={itemVariants} className={`${isDarkMode ? THEME_COLORS.dark.cardBg : THEME_COLORS.light.cardBg} p-8 rounded-2xl shadow-xl border ${isDarkMode ? THEME_COLORS.dark.cardBorder : THEME_COLORS.light.cardBorder}`}>
              <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Attrition by Department</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={attritionData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? THEME_COLORS.dark.chartStroke : THEME_COLORS.light.chartStroke} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                  <YAxis axisLine={false} tickLine={false} stroke={isDarkMode ? THEME_COLORS.dark.axisStroke : THEME_COLORS.light.axisStroke} />
                  <Tooltip content={<CustomTooltip theme={currentTheme} />} />
                  <Bar dataKey="Attrition" radius={[10, 10, 0, 0]}>
                    {attritionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={THEME_COLORS.dark.headcountResigned} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        </section>

        <hr className={`my-16 border-t-2 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`} />

        {/* CV Banking Management */}
        <section className="mb-16">
          <motion.h3
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className={`text-3xl font-bold mb-8 flex items-center ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          >
            <DocumentTextIcon className={`h-8 w-8 mr-3 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            CV Banking Management
          </motion.h3>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <motion.div variants={itemVariants}>
              <StatCard
                title="Total CV Bank Size"
                value="15,000"
                icon={<BriefcaseIcon className={`h-8 w-8 ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`} />}
                iconBg={isDarkMode ? "bg-yellow-900" : "bg-yellow-200"}
                description="Total resumes in the database"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="New Uploads Today"
                value="25"
                icon={<ArrowDownCircleIcon className={`h-8 w-8 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />}
                iconBg={isDarkMode ? "bg-indigo-900" : "bg-indigo-200"}
                description="New resumes added today"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Active Candidates"
                value="340"
                icon={<UsersIcon className={`h-8 w-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />}
                iconBg={isDarkMode ? "bg-blue-900" : "bg-blue-200"}
                description="Candidates in active pipelines"
                theme={currentTheme}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <StatCard
                title="Top Skills"
                value="Python"
                icon={<MagnifyingGlassIcon className={`h-8 w-8 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />}
                iconBg={isDarkMode ? "bg-green-900" : "bg-green-200"}
                description="Most common skill in the database"
                theme={currentTheme}
              />
            </motion.div>
          </motion.div>
        </section>
      </div>
    </motion.div>
  );
};

export default MISDashboard;